export const services = [
  {
    title: "Search Engine Optimization (SEO)",
    slug: "seo",
    description:
      "Boost your website’s visibility and rank higher on search engines with our result-driven SEO strategies.",
    fullContent: `Our comprehensive SEO services are built to deliver long-term search visibility, targeted traffic, and measurable ROI. We begin with a detailed audit, followed by keyword strategy, on-page improvements, technical fixes, and off-page link-building. Whether you're a startup or an enterprise, our SEO strategies are tailored to match your business goals. We also stay updated with Google algorithm changes to ensure your site remains compliant and competitive.`,

    coreServices: [
      {
        title: "Keyword Research",
        description:
          "We conduct in-depth keyword research to uncover high-value search terms, competitor gaps, and long-tail opportunities. This helps ensure your content targets the right audience and drives qualified traffic.",
        icon: "FaSearch",
      },
      {
        title: "On-Page SEO",
        description:
          "We optimize your site’s structure, content, meta tags, images, and internal linking to ensure maximum crawlability and relevance, improving your chances of ranking for targeted keywords.",
        icon: "FaEdit",
      },
      {
        title: "Off-Page SEO",
        description:
          "Our team builds high-authority backlinks through ethical strategies like guest posting, outreach, and digital PR to increase your domain authority and organic search trust.",
        icon: "FaLink",
      },
      {
        title: "Technical SEO",
        description:
          "From fixing crawl errors and improving site speed to enhancing mobile usability and implementing schema markup, we ensure your website meets technical search engine requirements.",
        icon: "FaCogs",
      },
      {
        title: "Local SEO",
        description:
          "We boost your visibility in local search results by optimizing your Google Business Profile, building citations, and ensuring accurate NAP consistency across local directories.",
        icon: "FaMapMarkerAlt",
      },
    ],

    features: [
      "Advanced Keyword Research & Competitor Gap Analysis",
      "On-Page SEO (Meta Tags, Schema Markup, Content Optimization)",
      "Technical SEO (Core Web Vitals, Crawling & Indexing, Site Architecture)",
      "High-Quality Link Building (Outreach, Guest Posts, Digital PR)",
      "Local SEO (Google Business Profile, Local Listings, Maps Optimization)",
      "E-commerce SEO (Product SEO, Category Optimization)",
      "Mobile SEO & Responsive Performance Tuning",
      "SEO Audits & Fix Implementation",
      "Monthly Reporting with Traffic, Rankings, and Conversions",
    ],

    benefits: [
      "Improved Google SERP rankings across relevant keywords",
      "Increased qualified organic traffic and leads",
      "Reduced dependency on paid ads",
      "Long-term digital brand authority",
      "Better website speed, structure, and user experience",
      "Higher trust and credibility in your industry",
    ],

    faqs: [
      {
        question: "How long does it take to see SEO results?",
        answer:
          "SEO is a long-term strategy. You can expect initial improvements within 3–6 months, with significant results over 6–12 months depending on your niche and competition.",
      },
      {
        question: "Do you offer local SEO for small businesses?",
        answer:
          "Yes. We help optimize your Google Business Profile, create local citations, and improve visibility in map packs and nearby searches.",
      },
      {
        question: "Will you provide regular reports and insights?",
        answer:
          "Absolutely. We share monthly reports detailing keyword rankings, traffic metrics, and actionable insights to improve your campaign.",
      },
      {
        question: "Can SEO help reduce paid ad spend?",
        answer:
          "Yes. With better organic visibility, businesses often reduce their reliance on paid ads while maintaining or even increasing traffic.",
      },
      {
        question: "Do you guarantee first-page rankings?",
        answer:
          "No ethical agency can guarantee rankings due to algorithm changes, but we follow best practices that significantly improve your chances of reaching top positions.",
      },
    ],

    toolsUsed: [
      { name: "Google Analytics", icon: "SiGoogleanalytics" },
      { name: "Ahrefs", icon: "SiAhrefs" },
      { name: "SEMRush", icon: "SiSemrush" },
    ],

    targetIndustries: [
      { name: "E-commerce", icon: "SiShopify" },
      { name: "Education", icon: "SiUdemy" },
      { name: "Healthcare", icon: "SiGooglehealth" },
    ],

    pricingNote:
      "Custom packages available based on keyword competitiveness, current site health, and target geography. Book a free SEO audit to get started.",
  },

  {
    title: "PPC Advertising",
    slug: "ppc-advertising",
    description:
      "Drive instant traffic and conversions with expertly managed Pay-Per-Click advertising campaigns.",
    fullContent: `Our PPC services are focused on performance and ROI. From Google Ads to Bing and social platforms, we create highly targeted campaigns using audience insights, A/B testing, and budget optimization. We handle keyword research, ad copywriting, landing page alignment, and analytics tracking to ensure every click counts.`,

    coreServices: [
      {
        title: "Campaign Setup & Management",
        description:
          "Creating and managing high-performing PPC campaigns across Google, Bing, and social platforms with targeted audience segmentation.",
        icon: "FaBullhorn",
      },
      {
        title: "Keyword & Competitor Research",
        description:
          "Analyzing the best keywords and competitors to maximize ROI and ad relevance.",
        icon: "FaSearch",
      },
      {
        title: "Ad Copywriting & Creative Testing",
        description:
          "Writing compelling ad copies and testing multiple creatives for optimal click-through rates.",
        icon: "FaPenFancy",
      },
      {
        title: "Landing Page Optimization",
        description:
          "Aligning landing pages with ad messaging for higher conversion rates.",
        icon: "FaDesktop",
      },
      {
        title: "Performance Tracking & Reporting",
        description:
          "Continuous monitoring, A/B testing, and detailed reporting to improve campaign effectiveness.",
        icon: "FaChartLine",
      },
    ],

    features: [
      "Google Ads & Microsoft Ads Management",
      "A/B Testing of Ad Creatives",
      "Conversion-Optimized Landing Pages",
      "Retargeting Campaigns",
      "Budget Allocation & Optimization",
      "Detailed Reporting & ROI Analysis",
    ],
    benefits: [
      "Instant visibility for your products/services",
      "Increased website traffic and leads",
      "Data-driven budget utilization",
      "Scalable campaigns for any industry",
      "Continuous performance tracking and refinement",
    ],
    faqs: [
      {
        question: "How quickly can PPC show results?",
        answer:
          "PPC can generate traffic as soon as your ads are live, often within a few hours.",
      },
      {
        question: "Do you manage display or video ads?",
        answer:
          "Yes, we manage all Google Ads formats including search, display, video, and shopping.",
      },
    ],
    toolsUsed: [
      { name: "Google Ads", icon: "SiGoogleads" },
      { name: "Microsoft Advertising", icon: "SiMicrosoftadvertising" },
      { name: "Google Analytics", icon: "SiGoogleanalytics" },
    ],
    targetIndustries: [
      { name: "Retail", icon: "SiAmazon" },
      { name: "Finance", icon: "SiVisa" },
      { name: "Education", icon: "SiUdemy" },
    ],
    pricingNote:
      "Budgets are flexible and can be scaled based on your industry and goals.",
  },

  {
    title: "Social Media Marketing",
    slug: "social-media",
    description:
      "Build brand presence and community with tailored social media campaigns across platforms.",
    fullContent: `We help brands connect with their audience on platforms like Facebook, Instagram, LinkedIn, and more. From organic content planning to paid social ads, we create strategies that spark engagement and conversions. Our services include post design, content calendars, influencer collaborations, and analytics reporting.`,

    coreServices: [
      {
        title: "Content Creation & Design",
        description:
          "Crafting visually appealing posts, stories, and videos tailored to each platform.",
        icon: "FaPaintBrush",
      },
      {
        title: "Platform Strategy & Management",
        description:
          "Developing platform-specific plans and managing day-to-day community engagement.",
        icon: "FaUsers",
      },
      {
        title: "Paid Social Advertising",
        description:
          "Running targeted paid ad campaigns to reach your ideal audience and boost conversions.",
        icon: "FaBullhorn",
      },
      {
        title: "Influencer Outreach & Collaborations",
        description:
          "Connecting brands with relevant influencers to amplify reach and authenticity.",
        icon: "FaHandshake",
      },
      {
        title: "Analytics & Reporting",
        description:
          "Monitoring performance and adjusting strategies based on data insights.",
        icon: "FaChartBar",
      },
    ],

    features: [
      "Content Calendar Creation",
      "Platform-Specific Strategy (FB, IG, LI)",
      "Social Ad Campaign Management",
      "Influencer Marketing & Outreach",
      "Community Management",
      "Performance Tracking & Reports",
    ],
    benefits: [
      "Stronger brand recall and engagement",
      "Higher audience loyalty and trust",
      "Multi-channel brand visibility",
      "Access to new customers via influencers",
    ],
    faqs: [
      {
        question: "Do you handle content creation?",
        answer:
          "Yes, we design graphics, write copy, and schedule posts tailored to your brand.",
      },
      {
        question: "Can you manage paid social ads?",
        answer:
          "Absolutely. We handle everything from audience targeting to optimization.",
      },
    ],
    toolsUsed: [
      { name: "Meta Business Suite", icon: "SiFacebook" },
      { name: "Buffer", icon: "SiBuffer" },
      { name: "Canva", icon: "SiCanva" },
    ],
    targetIndustries: [
      { name: "Fashion", icon: "SiAdidas" },
      { name: "Beauty", icon: "SiSephora" },
      { name: "Events", icon: "SiEventbrite" },
    ],
    pricingNote:
      "We offer monthly SMM packages including content, ads, and reporting.",
  },

  {
    title: "Email Marketing",
    slug: "email-marketing",
    description:
      "Nurture leads and retain customers through well-crafted email automation campaigns.",
    fullContent: `Our email marketing strategies are designed to drive engagement, repeat sales, and brand trust. We create high-converting email sequences, newsletters, and personalized drip campaigns using industry-leading tools.`,

    coreServices: [
      {
        title: "Email Campaign Design",
        description:
          "Creating engaging email templates and newsletters aligned with your brand.",
        icon: "FaEnvelope",
      },
      {
        title: "Automation & Drip Campaigns",
        description:
          "Setting up automated email sequences to nurture leads and boost retention.",
        icon: "FaRobot",
      },
      {
        title: "List Segmentation",
        description:
          "Dividing your audience into targeted groups for personalized messaging.",
        icon: "FaUsersCog",
      },
      {
        title: "A/B Testing",
        description:
          "Testing subject lines, content, and CTAs to optimize open and click rates.",
        icon: "FaVial",
      },
      {
        title: "Analytics & Reporting",
        description: "Tracking key metrics to improve campaign effectiveness.",
        icon: "FaChartLine",
      },
    ],

    features: [
      "Newsletter Campaigns",
      "Automated Email Sequences",
      "Customer Segmentation",
      "A/B Testing for Subject Lines & CTAs",
      "Reports on Open & Click Rates",
    ],
    benefits: [
      "Higher customer retention",
      "Personalized user journeys",
      "Consistent lead nurturing",
      "Improved engagement and conversions",
    ],
    faqs: [
      {
        question: "Which email tools do you use?",
        answer:
          "We work with Mailchimp, Sendinblue, Klaviyo, and others based on your needs.",
      },
    ],
    toolsUsed: [
      { name: "Mailchimp", icon: "SiMailchimp" },
      { name: "Sendinblue", icon: "SiSendinblue" },
      { name: "Klaviyo", icon: "SiKlaviyo" },
    ],
    targetIndustries: [
      { name: "E-commerce", icon: "SiShopify" },
      { name: "SaaS", icon: "SiSlack" },
      { name: "Travel", icon: "SiTripadvisor" },
    ],
    pricingNote:
      "Pricing depends on list size and email volume. Free audits available.",
  },

  {
    title: "Content Marketing",
    slug: "content-marketing",
    description:
      "Attract, engage, and convert with content that speaks to your audience’s needs.",
    fullContent: `We develop comprehensive content strategies, create blogs, guides, case studies, and visual assets that build authority and trust. Our writers and SEO experts collaborate to ensure every piece drives results.`,

    coreServices: [
      {
        title: "Content Strategy & Planning",
        description:
          "Developing editorial calendars and strategic content plans aligned with business goals.",
        icon: "FaClipboardList",
      },
      {
        title: "Blog & Article Writing",
        description:
          "Producing SEO-optimized blog posts and articles that engage and inform.",
        icon: "FaFileAlt",
      },
      {
        title: "Case Studies & Whitepapers",
        description:
          "Creating in-depth resources to demonstrate expertise and build trust.",
        icon: "FaBookOpen",
      },
      {
        title: "SEO Content Optimization",
        description:
          "Integrating keywords and best practices to enhance organic reach.",
        icon: "FaSearch",
      },
      {
        title: "Content Repurposing",
        description:
          "Turning existing content into multiple formats such as videos, infographics, and social posts.",
        icon: "FaSyncAlt",
      },
    ],

    features: [
      "Content Strategy & Planning",
      "Blog Writing & Optimization",
      "Whitepapers & Case Studies",
      "SEO Content & Topic Clustering",
      "Social Content & Repurposing",
    ],
    benefits: [
      "Improved search visibility",
      "Establish industry authority",
      "Increased lead generation",
      "Long-term content assets",
    ],
    faqs: [
      {
        question: "Can you handle industry-specific writing?",
        answer: "Yes, we have experienced writers across multiple verticals.",
      },
    ],
    toolsUsed: [
      { name: "Grammarly", icon: "SiGrammarly" },
      { name: "SurferSEO", icon: "SiSurfer" },
      { name: "Google Docs", icon: "SiGoogledocs" },
    ],
    targetIndustries: [
      { name: "Tech", icon: "SiGooglecloud" },
      { name: "Healthcare", icon: "SiGooglehealth" },
      { name: "B2B", icon: "SiNotion" },
    ],
    pricingNote: "Flexible packages based on content volume and formats.",
  },

  {
    title: "Branding Strategy",
    slug: "branding-strategy",
    description:
      "Define your brand’s personality, values, and messaging with a strategic identity approach.",
    fullContent: `Our branding experts help you establish or refresh your brand identity with clear messaging, compelling visuals, and emotional resonance. We align your brand story across all touchpoints for consistent recognition.`,

    coreServices: [
      {
        title: "Brand Positioning & Voice",
        description:
          "Defining your unique market position and consistent brand tone.",
        icon: "FaBullseye",
      },
      {
        title: "Logo & Visual Identity Design",
        description:
          "Creating memorable logos and visual assets that represent your brand.",
        icon: "FaPenNib",
      },
      {
        title: "Messaging & Tagline Development",
        description: "Crafting compelling slogans and messaging frameworks.",
        icon: "FaCommentAlt",
      },
      {
        title: "Color & Typography Systems",
        description:
          "Selecting brand colors and fonts that convey the right emotions.",
        icon: "FaPalette",
      },
      {
        title: "Brand Guidelines Creation",
        description:
          "Documenting brand usage rules to ensure consistency across channels.",
        icon: "FaBook",
      },
    ],

    features: [
      "Brand Positioning & Voice Development",
      "Logo & Visual Identity",
      "Tagline, Slogan & Messaging Guide",
      "Color Psychology & Typography Systems",
      "Brand Style Guide & Usage",
    ],
    benefits: [
      "Stronger market differentiation",
      "Emotional connection with customers",
      "Consistent brand messaging",
      "Professional & polished identity",
    ],
    faqs: [
      {
        question: "Is this suitable for startups?",
        answer: "Absolutely. We help build new brands from the ground up.",
      },
    ],
    toolsUsed: [
      { name: "Adobe Illustrator", icon: "SiAdobeillustrator" },
      { name: "Figma", icon: "SiFigma" },
      { name: "Coolors", icon: "SiColors" },
    ],
    targetIndustries: [
      { name: "Startups", icon: "SiStartrek" },
      { name: "Agencies", icon: "SiAirbnb" },
      { name: "Personal Brands", icon: "SiYcombinator" },
    ],
    pricingNote: "Fixed and custom branding packages are available.",
  }, //Branding Strategy

  {
    title: "Website Development",
    slug: "website-development",
    description:
      "Build fast, responsive, and modern websites that convert visitors into customers.",
    fullContent: `Our web development services combine UI/UX design, responsive code, and performance best practices. From landing pages to full-scale platforms, we use cutting-edge frameworks to create secure, scalable websites.`,
    features: [
      "Responsive Design & Development",
      "CMS Integration (WordPress, Webflow)",
      "Performance Optimization",
      "E-commerce Development",
      "Custom Features & API Integration",
    ],
    benefits: [
      "Fast-loading and mobile-friendly design",
      "Improved user experience",
      "SEO-optimized site structure",
      "Easy content management",
    ],
    faqs: [
      {
        question: "Do you build custom designs?",
        answer:
          "Yes, all websites are tailored to your branding and user journey.",
      },
    ],
    toolsUsed: [
      { name: "WordPress", icon: "SiWordpress" },
      { name: "Webflow", icon: "SiWebflow" },
      { name: "Vercel", icon: "SiVercel" },
    ],
    targetIndustries: [
      { name: "Agencies", icon: "SiNotion" },
      { name: "Retail", icon: "SiAmazon" },
      { name: "Services", icon: "SiZendesk" },
    ],
    pricingNote: "Custom quotes based on functionality, pages, and design.",
    coreServices: [
      {
        title: "UI/UX Design",
        description:
          "We design intuitive interfaces that align with your brand identity and improve user engagement across devices.",
        icon: "FaPencilRuler",
      },
      {
        title: "Frontend Development",
        description:
          "We code responsive, pixel-perfect layouts using modern JavaScript frameworks for fast and fluid experiences.",
        icon: "FaCode",
      },
      {
        title: "Backend Integration",
        description:
          "Seamless integration with databases, APIs, and CMS platforms to power your site with dynamic content and logic.",
        icon: "FaServer",
      },
      {
        title: "E-commerce Functionality",
        description:
          "We implement robust shopping carts, product filters, secure payment gateways, and order management features.",
        icon: "FaShoppingCart",
      },
      {
        title: "Website Deployment",
        description:
          "We launch and host your site using performance-optimized platforms like Vercel or Netlify with version control.",
        icon: "FaCloudUploadAlt",
      },
    ],
  }, // Website Developemnt

  {
    title: "App Development",
    slug: "app-development",
    description:
      "Craft powerful, scalable mobile and web applications tailored to your business goals.",
    fullContent: `Our app development services cover everything from ideation to deployment. Whether it's a cross-platform mobile app or a full-featured web app, we use the latest technologies to deliver secure, scalable, and high-performance applications.`,
    features: [
      "Cross-platform Mobile Development",
      "Custom Web Applications",
      "Progressive Web Apps (PWAs)",
      "API & Database Integration",
      "App Store Deployment (iOS & Android)",
    ],
    benefits: [
      "Reach users on Android, iOS, and the web",
      "Faster time-to-market with scalable architecture",
      "Improved user engagement and retention",
      "Custom features built to your business logic",
    ],
    faqs: [
      {
        question: "Do you build both iOS and Android apps?",
        answer:
          "Yes, we specialize in cross-platform development using technologies like Flutter and React Native to ensure smooth experiences on both platforms.",
      },
      {
        question: "Can you integrate third-party APIs?",
        answer:
          "Absolutely. We can connect your app with payment gateways, social logins, CRM systems, and more.",
      },
    ],
    toolsUsed: [
      { name: "React Native", icon: "SiReact" },
      { name: "Flutter", icon: "SiFlutter" },
      { name: "Firebase", icon: "SiFirebase" },
      { name: "Node.js", icon: "SiNodedotjs" },
    ],
    targetIndustries: [
      { name: "Healthcare", icon: "SiMediamarkt" },
      { name: "Education", icon: "SiGoogleclassroom" },
      { name: "E-commerce", icon: "SiShopify" },
      { name: "Logistics", icon: "SiGoogletagmanager" },
    ],
    pricingNote:
      "Pricing varies based on platform, features, and integrations. Get a custom quote today.",
    coreServices: [
      {
        title: "App UI/UX Design",
        description:
          "We craft intuitive and visually engaging interfaces that drive user satisfaction and retention.",
        icon: "FaMobileAlt",
      },
      {
        title: "Cross-platform Development",
        description:
          "We develop apps using frameworks like React Native or Flutter to ensure native performance across devices.",
        icon: "FaCodeBranch",
      },
      {
        title: "Backend Development",
        description:
          "Robust backend solutions using Node.js, Firebase, or custom APIs to handle your app's logic and data securely.",
        icon: "FaDatabase",
      },
      {
        title: "App Testing & QA",
        description:
          "Rigorous testing for performance, security, and UX to ensure a bug-free user experience.",
        icon: "FaBug",
      },
      {
        title: "App Store Deployment",
        description:
          "We handle the entire publishing process, from compliance to launch on Google Play and the Apple App Store.",
        icon: "FaCloudUploadAlt",
      },
    ],
  }, // App Development

  {
    title: "Analytics & Reporting",
    slug: "analytics",
    description:
      "Get deep insights into your digital performance and make data-driven decisions.",
    fullContent: `We help you set up, configure, and interpret analytics across your digital assets. From Google Analytics 4 to custom dashboards, we deliver insights that improve ROI and user journeys.`,
    features: [
      "GA4 Setup & Custom Events",
      "Funnel & Goal Tracking",
      "E-commerce Metrics Integration",
      "Monthly Dashboard Reporting",
      "User Behavior & Heatmaps",
    ],
    benefits: [
      "Clear view of campaign performance",
      "Better budget allocation",
      "Improved user journey understanding",
      "Custom reports tailored to KPIs",
    ],
    faqs: [
      {
        question: "Can I access real-time data?",
        answer:
          "Yes, we provide real-time and scheduled dashboards using GA4 and Data Studio.",
      },
    ],
    toolsUsed: [
      { name: "Google Analytics 4", icon: "SiGoogleanalytics" },
      { name: "Looker Studio", icon: "SiLooker" },
      { name: "Hotjar", icon: "SiHotjar" },
    ],
    targetIndustries: [
      { name: "Marketing", icon: "SiCampaignmonitor" },
      { name: "E-commerce", icon: "SiShopify" },
      { name: "Technology", icon: "SiGooglecloud" },
    ],
    pricingNote: "Audits and monthly reporting packages available.",
    coreServices: [
      {
        title: "GA4 Configuration",
        description:
          "We set up Google Analytics 4 with event-based tracking for accurate, privacy-compliant insights.",
        icon: "FaChartLine",
      },
      {
        title: "Goal & Funnel Tracking",
        description:
          "Track key user actions like form fills, purchases, and conversions with detailed funnel views.",
        icon: "FaBullseye",
      },
      {
        title: "Custom Dashboards",
        description:
          "Build real-time dashboards in Looker Studio for marketing, product, and executive reporting.",
        icon: "FaChartBar",
      },
      {
        title: "E-commerce Analytics",
        description:
          "Measure revenue, AOV, product performance, and cart abandonment rates for your store.",
        icon: "FaShoppingBag",
      },
      {
        title: "Behavioral Insights",
        description:
          "Use tools like Hotjar to analyze scroll maps, clicks, and heatmaps to refine UX and content strategy.",
        icon: "FaMousePointer",
      },
    ],
  },
];
